import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

export function Contrails({ 
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  color = '#88ccff',
  opacity = 0.8,
  ...props
}) {
  const meshRef = useRef()
  const timeRef = useRef(0)
  
  // Create ribbon geometry - two crossed planes merged into one
  const geometry = useMemo(() => {
    const geo1 = new THREE.PlaneGeometry(10, 1, 64, 8)
    const geo2 = new THREE.PlaneGeometry(10, 1, 64, 8)
    
    // Rotate the second plane 90 degrees
    geo2.rotateX(Math.PI / 2)
    
    // Translate both so origin is at one end
    geo1.translate(5, 0, 0)
    geo2.translate(5, 0, 0)
    
    // Merge into single geometry
    const mergedGeo = mergeGeometries([geo1, geo2])
    
    return mergedGeo
  }, [])
  
  // Create simple shader material with ribbon distortion
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(color) },
        uOpacity: { value: opacity },
      },
      vertexShader: `
        uniform float uTime;
        varying vec2 vUv;
        varying float vDistortion;
        
        void main() {
          vUv = uv;
          
          vec3 pos = position;
          
          // Create wave distortion along the ribbon (increased amplitude)
          float wave = sin(uv.x * 3.14159 + uTime) * 0.8;
          pos.y += wave * uv.x; // Distort more towards the end
          
          // Add some turbulence (increased amplitude)
          float turbulence = sin(uv.x * 10.0 + uTime * -2.0) * 1.0;
          pos.z += turbulence * uv.x;
          
          // Add additional swirling motion
          float swirl = cos(uv.x * 6.28 + uTime * 1.5) * 0.4;
          pos.y += swirl * uv.x * uv.x;
          
          vDistortion = wave;
          
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uOpacity;
        varying vec2 vUv;
        varying float vDistortion;
        
        void main() {
          // Fade in from the start, fade out at the tip
          float fade = smoothstep(0.0, 0.15, vUv.x) * (1.0 - smoothstep(0.85, 1.0, vUv.x));
          
          // Fade from edges
          float edgeFade = 1.0 - abs(vUv.y - 0.5) * 2.0;
          edgeFade = smoothstep(0.0, 0.5, edgeFade);
          
          float finalAlpha = fade * edgeFade * uOpacity;
          
          gl_FragColor = vec4(uColor, finalAlpha);
        }
      `,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  }, [color, opacity])
  
  // Animate the ribbon
  useFrame((state, delta) => {
    timeRef.current += delta
    if (material.uniforms) {
      material.uniforms.uTime.value = timeRef.current
    }
  })
  
  return (
    <mesh 
      ref={meshRef}
      position={position} 
      rotation={rotation} 
      scale={scale}
      geometry={geometry}
      material={material}
      {...props}
    />
  )
}